/** PeerPort on a real RTCPeerConnection (audio only). */
import type { IceServer } from '@cube-rush/shared';
import type {
  IceCandidate,
  MicCapture,
  PeerPort,
  PeerPortEvents,
  SessionDescription,
} from './media';
import type { VoiceOutput } from './voiceOutput';

/** The track behind a MicCapture of this module's media layer. */
export type TrackOf = (mic: MicCapture | null) => MediaStreamTrack | null;

export class BrowserPeerPort implements PeerPort {
  private readonly pc: RTCPeerConnection;
  private transceiver: RTCRtpTransceiver | null = null;
  private mic: MicCapture | null = null;
  private iceRestartPending = false;
  private hasRemoteTrack = false;

  constructor(
    iceServers: readonly IceServer[],
    private readonly events: PeerPortEvents,
    output: VoiceOutput,
    private readonly trackOf: TrackOf,
  ) {
    this.pc = new RTCPeerConnection({
      iceServers: toRtcIceServers(iceServers),
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
    });
    this.pc.onnegotiationneeded = () => events.negotiationNeeded();
    this.pc.onicecandidate = (event) => {
      events.iceCandidate(event.candidate ? candidateOf(event.candidate) : null);
    };
    this.pc.onconnectionstatechange = () => events.connectionState(this.pc.connectionState);
    this.pc.ontrack = (event) => {
      if (event.track.kind !== 'audio' || this.hasRemoteTrack) return;
      this.hasRemoteTrack = true;
      events.remoteAudio(output.attach(event.track, event.streams[0] ?? null, event.receiver));
    };
  }

  get signalingState(): RTCSignalingState {
    return this.pc.signalingState;
  }

  get connectionState(): RTCPeerConnectionState {
    return this.pc.connectionState;
  }

  get iceGatheringState(): RTCIceGatheringState {
    return this.pc.iceGatheringState;
  }

  get localDescription(): SessionDescription | null {
    return descriptionOf(this.pc.localDescription);
  }

  get remoteDescription(): SessionDescription | null {
    return descriptionOf(this.pc.remoteDescription);
  }

  addAudioTransceiver(): void {
    if (this.transceiver) return;
    this.transceiver = this.pc.addTransceiver('audio', { direction: 'sendrecv' });
    this.applyMic();
  }

  adoptAudioTransceiver(): boolean {
    if (this.transceiver) return true;
    const offered = this.pc
      .getTransceivers()
      .find((t) => t.receiver.track.kind === 'audio' && t.currentDirection !== 'stopped');
    if (!offered) return false;
    offered.direction = 'sendrecv';
    this.transceiver = offered;
    this.applyMic();
    return true;
  }

  setMic(mic: MicCapture | null): void {
    this.mic = mic;
    this.applyMic();
  }

  async setLocalDescription(): Promise<void> {
    if (this.iceRestartPending && this.pc.signalingState === 'stable') {
      this.iceRestartPending = false;
      await this.pc.setLocalDescription(await this.pc.createOffer({ iceRestart: true }));
      return;
    }
    try {
      await this.pc.setLocalDescription();
    } catch (error) {
      // Browsers without the argument-less form (older Safari).
      if (!(error instanceof TypeError)) throw error;
      const description =
        this.pc.signalingState === 'have-remote-offer'
          ? await this.pc.createAnswer()
          : await this.pc.createOffer();
      await this.pc.setLocalDescription(description);
    }
  }

  async setRemoteDescription(description: SessionDescription): Promise<void> {
    await this.pc.setRemoteDescription(description);
  }

  async addIceCandidate(candidate: IceCandidate): Promise<void> {
    await this.pc.addIceCandidate(candidate);
  }

  restartIce(iceServers: readonly IceServer[]): void {
    try {
      this.pc.setConfiguration({
        ...this.pc.getConfiguration(),
        iceServers: toRtcIceServers(iceServers),
      });
    } catch {
      // Keep the old servers.
    }
    if (typeof this.pc.restartIce === 'function') {
      this.pc.restartIce();
    } else {
      this.iceRestartPending = true;
      this.events.negotiationNeeded();
    }
  }

  close(): void {
    this.pc.onnegotiationneeded = null;
    this.pc.onicecandidate = null;
    this.pc.onconnectionstatechange = null;
    this.pc.ontrack = null;
    this.pc.close();
  }

  private applyMic(): void {
    const sender = this.transceiver?.sender;
    if (!sender) return;
    const track = this.trackOf(this.mic);
    if (sender.track === track) return;
    sender.replaceTrack(track).catch(() => {
      // The connection is closing; nothing to attach to.
    });
  }
}

function toRtcIceServers(servers: readonly IceServer[]): RTCIceServer[] {
  return servers.map((server) => {
    const ice: RTCIceServer = { urls: Array.isArray(server.urls) ? [...server.urls] : server.urls };
    if (server.username !== undefined) ice.username = server.username;
    if (server.credential !== undefined) ice.credential = server.credential;
    return ice;
  });
}

function descriptionOf(description: RTCSessionDescription | null): SessionDescription | null {
  if (!description) return null;
  return { type: description.type, sdp: description.sdp };
}

function candidateOf(candidate: RTCIceCandidate): IceCandidate {
  const init = candidate.toJSON();
  const result: IceCandidate = { candidate: init.candidate ?? '' };
  if (init.sdpMid !== undefined) result.sdpMid = init.sdpMid;
  if (init.sdpMLineIndex !== undefined) result.sdpMLineIndex = init.sdpMLineIndex;
  if (init.usernameFragment !== undefined) result.usernameFragment = init.usernameFragment;
  return result;
}
